// 手机端使用单列输入与可换行工具栏，长地址完整阅读；电脑保留原布局，避免窄屏遮住表单或操作。
// 手机将明细按记录展示，保留原行事件；有交互表头或复杂行时保留可滑动表格，避免丢失排序、勾选和操作。
import { ResponsiveTable } from "../components/mobile/ResponsiveTable";
import React, { useEffect, useState, useCallback } from "react";
import { useApp } from "../context/AppContext";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "../components/ui/sonner";
import { Warehouse, Boxes, PackageX, Truck, ArrowDownToLine, ArrowUpFromLine, Loader2, AlertTriangle, ArrowLeftRight, ShoppingCart } from "lucide-react";
import { ink } from "../lib/ink";

const money = (n) => "$" + Number(n || 0).toLocaleString();
const inp = "mt-1 w-full text-sm rounded-md border border-slate-200 px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-400";
const PO_ROLES = ["admin", "ceo", "eng_manager", "pm", "warehouse_manager", "material_purchaser"];
const TRANSFER_ROLES = ["admin", "ceo", "eng_manager", "pm", "member", "warehouse_manager"];

export default function WarehouseDashboard() {
  const { api, lang, user } = useApp();
  const zh = lang === "zh";
  const nav = useNavigate();
  const [ov, setOv] = useState(null);
  const [low, setLow] = useState(null);
  const [sugg, setSugg] = useState([]);
  const [po, setPo] = useState(null);
  const [moving, setMoving] = useState("");
  const canPO = PO_ROLES.includes(user.role);
  const canTransfer = TRANSFER_ROLES.includes(user.role);

  const reload = useCallback(() => {
    api.get("/warehouses/overview").then(({ data }) => setOv(data)).catch(() => setOv({ cards: [], totalValue: 0, totalIn: 0, totalOut: 0, totalPending: 0 }));
    api.get("/warehouses/low-stock").then(({ data }) => setLow(data.rows || [])).catch(() => setLow([]));
    api.get("/warehouses/transfer-suggestions").then(({ data }) => setSugg(data.suggestions || [])).catch(() => setSugg([]));
  }, [api]);
  useEffect(() => { reload(); }, [reload]);

  const doTransfer = async (s) => {
    if (!window.confirm(zh ? `确认从「${s.fromWarehouseName}」调拨 ${s.suggestQty} ${s.unit || ""} ${s.name} 到「${s.toWarehouseName}」？` : "Confirm transfer?")) return;
    setMoving(`${s.fromWarehouseId}-${s.toWarehouseId}-${s.key}`);
    try {
      await api.post(`/warehouses/${s.fromWarehouseId}/transfer`, { toWarehouseId: s.toWarehouseId, key: s.key, qty: s.suggestQty });
      toast.success(zh ? `已调拨 ${s.suggestQty} ${s.unit || ""} ${s.name} → ${s.toWarehouseName}` : "Transferred");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setMoving("");
  };

  if (!ov || low === null) return <div className="text-sm text-slate-400 py-14 text-center" data-testid="whd-loading"><Loader2 className="h-4 w-4 animate-spin inline mr-1" />{zh ? "加载中…" : "Loading…"}</div>;
  const lowTotal = low.length;
  const outOfStock = low.filter((r) => Number(r.qty || 0) <= 0).length;

  return (
    <div className="space-y-5" data-testid="warehouse-dashboard">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Warehouse className="h-6 w-6 text-red-600" />{zh ? "仓储看板" : "Warehouse Dashboard"}</h1>
        <p className="text-slate-500 text-sm">{zh ? `库存总值与低库存预警一览 · ${ov.month || ""}` : "Stock value & low-stock alerts at a glance"}</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="whd-kpis">
        <Kpi tid="whd-kpi-value" label={zh ? "库存总值" : "Stock Value"} value={money(ov.totalValue)} icon={Boxes} color="#0f172a" />
        <Kpi tid="whd-kpi-low" label={zh ? "低库存预警" : "Low Stock"} value={lowTotal} icon={PackageX} color={lowTotal ? "#dc2626" : "#16a34a"} pulse={lowTotal > 0} />
        <Kpi tid="whd-kpi-in" label={zh ? "本月入库" : "Inbound (mo)"} value={money(ov.totalIn)} icon={ArrowDownToLine} color="#16a34a" />
        <Kpi tid="whd-kpi-out" label={zh ? "本月出库" : "Outbound (mo)"} value={money(ov.totalOut)} icon={ArrowUpFromLine} color="#d97706" />
        <Kpi tid="whd-kpi-pending" label={zh ? "待到货单" : "Pending Deliveries"} value={ov.totalPending} icon={Truck} color="#2563eb" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="whd-warehouse-cards">
        {(ov.cards || []).map((w) => (
          <button key={w.id} data-testid={`whd-card-${w.id}`} onClick={() => nav("/warehouses")}
            className="bg-white rounded-xl border border-slate-200 p-4 text-left hover:border-brand-300 transition-colors">
            <div className="flex items-center justify-between">
              <p className="font-semibold text-slate-900 truncate">{w.name}</p>
              <span className="text-[10px] text-slate-400 shrink-0">{w.regionName}</span>
            </div>
            <p className="text-xl font-extrabold text-slate-900 mt-1">{money(w.stockValue)}</p>
            <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-slate-500 mt-1.5">
              <span>{w.skuCount} {zh ? "种材料" : "SKUs"}</span>
              <span className={w.lowCount ? "text-red-600 font-semibold" : "text-green-600"}>{w.lowCount ? `${w.lowCount} ${zh ? "项低库存" : "low"}` : (zh ? "库存健康" : "healthy")}</span>
              {w.pending > 0 && <span className="text-blue-600">{w.pending} {zh ? "待到货" : "pending"}</span>}
            </div>
          </button>
        ))}
        {(ov.cards || []).length === 0 && <p className="text-sm text-slate-300 py-8 col-span-full text-center">{zh ? "暂无仓库" : "No warehouses"}</p>}
      </div>

      {/* transfer suggestions */}
      {sugg.length > 0 && (
        <div className="bg-white rounded-xl border border-blue-200 overflow-hidden" data-testid="whd-transfer-suggestions">
          <div className="flex flex-wrap items-center gap-2 px-5 py-3 border-b border-blue-100 bg-blue-50/50">
            <ArrowLeftRight className="h-4 w-4 text-blue-600" />
            <h3 className="font-semibold text-slate-900">{zh ? "仓库间调拨建议" : "Transfer Suggestions"}</h3>
            <span className="text-xs font-semibold text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full" data-testid="whd-sugg-count">{sugg.length} {zh ? "条" : ""}</span>
            <span className="text-[11px] text-slate-400">{zh ? "缺货仓 ← 富余仓（超阈值部分），一键调拨" : "short ← surplus, one-click"}</span>
          </div>
          <div className="overflow-x-auto">
            <ResponsiveTable className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500"><tr>
                <th className="text-left font-medium px-5 py-2.5">{zh ? "材料" : "Material"}</th>
                <th className="text-left font-medium px-4 py-2.5">{zh ? "从（富余仓）" : "From (surplus)"}</th>
                <th className="text-left font-medium px-4 py-2.5">{zh ? "到（缺货仓）" : "To (short)"}</th>
                <th className="text-right font-medium px-4 py-2.5">{zh ? "建议调拨" : "Suggest"}</th>
                <th className="px-4 py-2.5"></th>
              </tr></thead>
              <tbody>
                {sugg.map((s, i) => {
                  const k = `${s.fromWarehouseId}-${s.toWarehouseId}-${s.key}`;
                  return (
                    <tr key={k} className="border-t border-slate-100" data-testid={`whd-sugg-row-${i}`}>
                      <td className="px-5 py-2.5 font-medium text-slate-800">{s.name}</td>
                      <td className="px-4 py-2.5 text-slate-600">{s.fromWarehouseName}<span className="text-[11px] text-green-600 ml-1.5">{zh ? "库存" : ""} {s.fromQty}（{zh ? "富余" : "surplus"} {s.fromSurplus}）</span></td>
                      <td className="px-4 py-2.5 text-slate-600">{s.toWarehouseName}<span className="text-[11px] text-red-600 ml-1.5">{s.toQty}/{zh ? "阈值" : "thr"} {s.threshold}</span></td>
                      <td className="px-4 py-2.5 text-right font-bold text-blue-700">{s.suggestQty} {s.unit || ""}</td>
                      <td className="px-4 py-2.5 text-right">
                        {canTransfer && (
                          <button data-testid={`whd-transfer-btn-${i}`} onClick={() => doTransfer(s)} disabled={moving === k}
                            className="text-xs text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 px-2.5 py-1 rounded-md inline-flex items-center gap-1">
                            <ArrowLeftRight className="h-3 w-3" />{moving === k ? (zh ? "调拨中…" : "…") : (zh ? "执行调拨" : "Transfer")}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </ResponsiveTable>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="whd-low-stock">
        <div className="flex flex-wrap items-center gap-2 px-5 py-3 border-b border-slate-100 bg-red-50/40">
          <PackageX className="h-4 w-4 text-brand-500" />
          <h3 className="font-semibold text-slate-900">{zh ? "低库存预警明细" : "Low Stock Alerts"}</h3>
          {lowTotal > 0 && <span className="text-xs font-semibold text-red-600 bg-red-100 px-2 py-0.5 rounded-full" data-testid="whd-low-count">{lowTotal} {zh ? "项" : ""}{outOfStock ? ` · ${outOfStock} ${zh ? "项缺货" : "out"}` : ""}</span>}
          <button data-testid="whd-goto-warehouses" onClick={() => nav("/warehouses")} className="ml-auto text-xs text-slate-500 hover:text-brand-500 underline">{zh ? "进入仓库管理补货 →" : "Restock →"}</button>
        </div>
        {lowTotal === 0 ? (
          <p className="text-sm text-slate-300 py-10 text-center">{zh ? "所有设置了阈值的材料库存充足 ✓" : "All stocked ✓"}</p>
        ) : (
          <div className="overflow-x-auto md:max-h-96 overflow-y-auto">
            <ResponsiveTable className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 sticky top-0"><tr>
                <th className="text-left font-medium px-5 py-2.5">{zh ? "材料" : "Material"}</th>
                <th className="text-left font-medium px-4 py-2.5">{zh ? "仓库" : "Warehouse"}</th>
                <th className="text-right font-medium px-4 py-2.5">{zh ? "当前库存" : "Qty"}</th>
                <th className="text-right font-medium px-4 py-2.5">{zh ? "预警阈值" : "Threshold"}</th>
                <th className="text-left font-medium px-4 py-2.5">{zh ? "状态" : "Status"}</th>
                <th className="px-4 py-2.5"></th>
              </tr></thead>
              <tbody>
                {low.map((r, i) => {
                  const oos = Number(r.qty || 0) <= 0;
                  return (
                    <tr key={`${r.warehouseId}-${r.key}`} className="border-t border-slate-100" data-testid={`whd-low-row-${i}`}>
                      <td className="px-5 py-2.5 font-medium text-slate-800">{r.name}</td>
                      <td className="px-4 py-2.5 text-slate-500">{r.warehouseName}<span className="text-[10px] text-slate-400 ml-1">{r.regionName}</span></td>
                      <td className={`px-4 py-2.5 text-right font-bold ${oos ? "text-red-600" : "text-amber-600"}`}>{r.qty}</td>
                      <td className="px-4 py-2.5 text-right text-slate-500">{r.threshold}</td>
                      <td className="px-4 py-2.5"><span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${oos ? "bg-red-100 text-red-700 animate-pulse" : "bg-amber-100 text-amber-700"}`}><AlertTriangle className="h-3 w-3" />{oos ? (zh ? "缺货" : "Out") : (zh ? "低库存" : "Low")}</span></td>
                      <td className="px-4 py-2.5 text-right">
                        {canPO && (
                          <button data-testid={`whd-po-btn-${i}`} onClick={() => setPo(r)}
                            className="text-xs text-white bg-brand-500 hover:bg-brand-600 px-2.5 py-1 rounded-md inline-flex items-center gap-1">
                            <ShoppingCart className="h-3 w-3" />{zh ? "生成采购单" : "Order"}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </ResponsiveTable>
          </div>
        )}
      </div>

      {po && <PoDialog zh={zh} api={api} row={po} onClose={() => setPo(null)} onSaved={() => { setPo(null); reload(); }} />}
    </div>
  );
}

function PoDialog({ zh, api, row, onClose, onSaved }) {
  const defQty = Math.max(Number(row.threshold || 0) * 2 - Number(row.qty || 0), Number(row.threshold || 1));
  const defDate = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);
  const [f, setF] = useState({ qty: defQty, unitPrice: "", supplier: "", expectedDate: defDate, note: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const submit = async () => {
    if (!Number(f.qty) || Number(f.qty) <= 0) return toast.error(zh ? "请填写采购数量" : "Qty required");
    if (!f.expectedDate) return toast.error(zh ? "请填写约定到货日期" : "Date required");
    setBusy(true);
    try {
      await api.post("/delivery-orders", {
        warehouseId: row.warehouseId, key: row.key, name: row.name,
        qty: Number(f.qty), unitPrice: Number(f.unitPrice) || 0,
        supplier: f.supplier, expectedDate: f.expectedDate,
        note: f.note || (zh ? "低库存补货（仓储看板生成）" : "Low-stock restock"),
      });
      toast.success(zh ? `已生成采购到货单：${row.name} x${f.qty}，可在仓库管理跟踪到货` : "PO created");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="whd-po-dialog">
        <DialogHeader>
          <DialogTitle>{zh ? "生成补货采购单" : "Restock Purchase Order"}</DialogTitle>
          <DialogDescription>{row.name} · {row.warehouseName} · {zh ? `当前 ${row.qty} / 阈值 ${row.threshold}` : `${row.qty}/${row.threshold}`}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="text-xs font-medium text-slate-500">{zh ? "采购数量" : "Qty"}
              <input data-testid="whd-po-qty" type="number" min="0" className={inp} value={f.qty} onChange={set("qty")} />
            </label>
            <label className="text-xs font-medium text-slate-500">{zh ? "单价 (可选)" : "Unit price"}
              <input data-testid="whd-po-price" type="number" min="0" className={inp} value={f.unitPrice} onChange={set("unitPrice")} placeholder="0" />
            </label>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="text-xs font-medium text-slate-500">{zh ? "供应商 (可选)" : "Supplier"}
              <input data-testid="whd-po-supplier" className={inp} value={f.supplier} onChange={set("supplier")} />
            </label>
            <label className="text-xs font-medium text-slate-500">{zh ? "约定到货日期" : "Expected date"}
              <input data-testid="whd-po-date" type="date" className={inp} value={f.expectedDate} onChange={set("expectedDate")} />
            </label>
          </div>
          <label className="text-xs font-medium text-slate-500 block">{zh ? "备注" : "Note"}
            <input data-testid="whd-po-note" className={inp} value={f.note} onChange={set("note")} placeholder={zh ? "低库存补货" : ""} />
          </label>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={onClose}>{zh ? "取消" : "Cancel"}</Button>
            <Button type="button" data-testid="whd-po-submit" disabled={busy} onClick={submit} className="bg-brand-500 hover:bg-brand-600">{busy ? (zh ? "生成中…" : "…") : (zh ? "生成采购单" : "Create PO")}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Kpi({ label, value, icon: Icon, color, tid, pulse }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid={tid}>
      <div className="flex items-center gap-2">
        <Icon className={`h-4 w-4 ${pulse ? "animate-pulse" : ""}`} style={ink(color)} />
        <p className="text-[11px] text-slate-400">{label}</p>
      </div>
      <p className="text-xl font-extrabold mt-1" style={ink(color)}>{value}</p>
    </div>
  );
}
